/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 *
 * Data-driven PDF export — mirrors the approach used by exportPagePptx.ts.
 * Data is fetched directly from the API (no DOM capture / screenshot).
 * KPI cards are drawn programmatically with jsPDF primitives.
 * GlobalChart sections embed an ECharts-rendered PNG image.
 */

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import dayjs from 'dayjs';
import { SiteChartsConfig } from '../../components/SiteCharts';
import {
  GLOBAL_LTE_CHART_GROUPS,
  GLOBAL_NR_CHART_GROUPS,
  BATCH_DATA_KEY_MAP,
  NR_BATCH_DATA_KEY_MAP,
} from './globalChartConfigs';
import { LOCAL_STORAGE } from '../../globals/globals';
import { getThemeAwareChartColors } from '../../theme/chartColors';
import { getAdaptiveScaleMin, getObservedKpiMin } from './kpiScale';

// ── Page dimensions (A4 landscape, mm) ───────────────────────────────────────
const PW = 297;
const PH = 210;

// ── ECharts offscreen canvas dimensions ──────────────────────────────────────
// Must match the PDF chart placement area (PW-10 × PH-22 mm) exactly so that
// jsPDF fills the space without letterboxing. Render at 5px/mm → ×2 pixelRatio
// gives ~254 DPI in the final PNG.
const PDF_CHART_PX_W = Math.round((PW - 10) * 5); // 1435 px  (≈ 287 mm)
const PDF_CHART_PX_H = Math.round((PH - 22) * 5); // 940 px   (≈ 188 mm)

// ── Design tokens as RGB triples ─────────────────────────────────────────────
type RGB = [number, number, number];
const C: Record<string, RGB> = {
  dark:       [30,  41,  59],   // #1E293B
  white:      [255, 255, 255],
  slate:      [148, 163, 184],  // #94A3B8
  cardBg:     [248, 250, 252],  // #F8FAFC
  border:     [226, 232, 240],  // #E2E8F0
  fg:         [15,  23,  42],   // #0F172A
  muted:      [100, 116, 139],  // #64748B
  green:      [196, 214, 221],  // #c4d6dd — bar fill ≥ target (light)
  red:        [254, 170, 113],  // #feaa71 — bar fill < target (light)
  teal:       [226, 232, 240],  // #E2E8F0 — unfilled track (muted gray)
  amber:      [226, 232, 240],  // #E2E8F0 — unfilled track (muted gray)
  deltaGreen: [51,  184, 145],  // #33b891
  deltaAmber: [255, 215, 73],   // #ffd749
  purple:     [127, 62,  237],  // #7f3eed
};

// ── jsPDF colour setters ──────────────────────────────────────────────────────
const fill   = (pdf: jsPDF, c: RGB) => pdf.setFillColor(c[0], c[1], c[2]);
const stroke = (pdf: jsPDF, c: RGB) => pdf.setDrawColor(c[0], c[1], c[2]);
const color  = (pdf: jsPDF, c: RGB) => pdf.setTextColor(c[0], c[1], c[2]);

// ── KPI field / card definitions (mirrors exportPagePptx.ts) ─────────────────

const LTE_KPI_FIELDS = [
  { key: 'dataAccessibilityPercent',        label: 'Data Accessibility' },
  { key: 'volteAccessibilityPercent',       label: 'VoLTE Accessibility' },
  { key: 'dataRetainabilityPercent',        label: 'Data Retainability' },
  { key: 'volteRetainabilityPercent',       label: 'VoLTE Retainability' },
  { key: 's1SuccessRatePercent',            label: 'S1 Success Rate' },
  { key: 'macroApHandinPercent',            label: 'Macro AP Hand-in' },
  { key: 'apMacroIntraHandoutPercent',      label: 'AP Macro Intra HO' },
  { key: 'apMacroInterFreqHandoutPercent',  label: 'AP Macro Inter-Freq HO' },
  { key: 'intraEranInterFreqPercent',       label: 'Intra ERAN Inter-Freq' },
  { key: 'intraEranIntraFreqPercent',       label: 'Intra ERAN Intra-Freq' },
  { key: 'x1HoIntraFreqPercent',            label: 'X1 HO Intra-Freq' },
  { key: 'x1HoInterFreqPercent',            label: 'X1 HO Inter-Freq' },
];

const NR_TABLE_KPI_FIELDS = [
  { key: 'endcDataAccessibilityPercent',        label: 'ENDC Data Accessibility' },
  { key: 'endcDataRetainabilityPercent',        label: 'ENDC Data Retainability' },
  { key: 'endcSgNbAdditionSuccessRatePercent',  label: 'SgNB Addition SR' },
  { key: 'endcIntraCuIntraFreqHoSrPercent',     label: 'Intra CU Intra-Freq HO SR' },
];

export interface KpiCardDef {
  title: string;
  field: string;
  scaleMin: number;
  target: number;
}

const LTE_KPI_CARDS: KpiCardDef[] = [
  { title: 'Data Accessibility',        field: 'dataAccessibilityPercent',       scaleMin: 98.5, target: 99.5 },
  { title: 'VoLTE Accessibility',       field: 'volteAccessibilityPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'Data Retainability',        field: 'dataRetainabilityPercent',       scaleMin: 98.5, target: 99.5 },
  { title: 'VoLTE Retainability',       field: 'volteRetainabilityPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'S1 Success Rate',           field: 's1SuccessRatePercent',           scaleMin: 99.0, target: 99.7 },
  { title: 'Macro AP Hand-in',          field: 'macroApHandinPercent',           scaleMin: 97.0, target: 98.5 },
  { title: 'AP Macro Intra Freq HO',    field: 'apMacroIntraHandoutPercent',     scaleMin: 98.5, target: 99.5 },
  { title: 'AP Macro Inter Freq HO',    field: 'apMacroInterFreqHandoutPercent', scaleMin: 98.5, target: 99.5 },
  { title: 'Intra ERAN Inter Freq',     field: 'intraEranInterFreqPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'Intra ERAN Intra Freq',     field: 'intraEranIntraFreqPercent',      scaleMin: 98.5, target: 99.5 },
  { title: 'X1 HO Intra Freq',          field: 'x1HoIntraFreqPercent',           scaleMin: 98.5, target: 99.5 },
  { title: 'X1 HO Inter Freq',          field: 'x1HoInterFreqPercent',           scaleMin: 98.0, target: 99.0 },
];

const NR_KPI_CARDS: KpiCardDef[] = [
  { title: 'ENDC Data Accessibility',    field: 'endcDataAccessibilityPercent',       scaleMin: 97.0, target: 98.5 },
  { title: 'ENDC Data Retainability',    field: 'endcDataRetainabilityPercent',       scaleMin: 97.0, target: 98.5 },
  { title: 'ENDC SgNB Addition SR',      field: 'endcSgNbAdditionSuccessRatePercent', scaleMin: 97.0, target: 98.5 },
  { title: 'Intra CU Intra Freq HO SR',  field: 'endcIntraCuIntraFreqHoSrPercent',   scaleMin: 97.0, target: 98.5 },
];

// ── Page chrome helpers ──────────────────────────────────────────────────────

function drawCoverPage(pdf: jsPDF, tabLabel: string, timeLabel: string, generatedLabel: string) {
  fill(pdf, C.dark);
  pdf.rect(0, 0, PW, PH, 'F');
  color(pdf, C.white);
  pdf.setFontSize(28);
  pdf.setFont('helvetica', 'bold');
  pdf.text(tabLabel, PW / 2, 88, { align: 'center' });
  color(pdf, C.slate);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text(timeLabel, PW / 2, 106, { align: 'center' });
  pdf.setFontSize(10);
  pdf.text(`Generated: ${generatedLabel}`, PW / 2, 120, { align: 'center' });
}

function drawTitleBar(pdf: jsPDF, left: string, right: string) {
  fill(pdf, C.dark);
  pdf.rect(0, 0, PW, 12, 'F');
  color(pdf, C.white);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text(left, 5, 8.5);
  color(pdf, C.slate);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text(right, PW - 5, 8.5, { align: 'right' });
}

function drawFooter(pdf: jsPDF, label: string) {
  color(pdf, C.slate);
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'italic');
  pdf.text(label, 5, PH - 3);
}

// ── KPI card drawing ─────────────────────────────────────────────────────────

function drawKpiCard(
  pdf: jsPDF,
  x: number, y: number, w: number, h: number,
  title: string, value: number,
  scaleMin: number, scaleMax: number, target: number,
  delta?: number,
  trendLabels?: string[],
  trendValues?: number[],
) {
  const observedMin = getObservedKpiMin(value, trendValues);
  const effectiveScaleMin = getAdaptiveScaleMin(observedMin, scaleMin);
  const aboveTarget = value >= target;
  const range     = Math.max(0.0001, scaleMax - effectiveScaleMin);
  const valuePct  = Math.min(1, Math.max(0, (value - effectiveScaleMin) / range));
  const targetPct = Math.min(1, Math.max(0, (target - effectiveScaleMin) / range));
  const compact = h <= 44;

  // Card background + border
  fill(pdf, C.cardBg);
  stroke(pdf, C.border);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(x, y, w, h, 2, 2, 'FD');

  // Title
  color(pdf, C.muted);
  pdf.setFontSize(6);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title.toUpperCase(), x + 3, y + 6);

  // Value
  color(pdf, C.fg);
  pdf.setFontSize(compact ? 14 : 16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${value.toFixed(2)}%`, x + 3, y + (compact ? 13.2 : 14.5));

  // Progress bar
  const barX = x + 4;
  const barY = y + (compact ? 16.8 : 18.5);
  const barW = w - 8;
  const barH = 2.5;

  // Track (teal = above target, amber = below)
  fill(pdf, aboveTarget ? C.teal : C.amber);
  pdf.setLineWidth(0);
  pdf.roundedRect(barX, barY, barW, barH, 0.8, 0.8, 'F');

  // Fill
  if (valuePct > 0) {
    fill(pdf, aboveTarget ? C.green : C.red);
    pdf.roundedRect(barX, barY, Math.max(1, barW * valuePct), barH, 0.8, 0.8, 'F');
  }

  // Target marker (vertical line through bar)
  fill(pdf, C.dark);
  pdf.rect(barX + barW * targetPct - 0.5, barY - 1.5, 1, barH + 3, 'F');

  // Scale labels
  const labelY = barY + barH + (compact ? 3.3 : 4);
  color(pdf, C.muted);
  pdf.setFontSize(5);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`${effectiveScaleMin}%`, barX, labelY);
  pdf.text(`${scaleMax}%`, barX + barW, labelY, { align: 'right' });
  const tgtX = Math.max(barX + 6, Math.min(barX + barW - 6, barX + barW * targetPct));
  pdf.text(`${target} tgt`, tgtX, labelY, { align: 'center' });

  // Sparkline
  const hasTrend = trendValues && trendValues.length >= 2;
  const sparkStartY = labelY + (compact ? 3.8 : 5);
  const sparkEndY   = y + h - (delta !== undefined ? (compact ? 6.8 : 10) : (compact ? 3 : 4));
  const sparkH      = Math.max(6, sparkEndY - sparkStartY);

  if (hasTrend) {
    const vals   = trendValues!;
    const vRange = Math.max(0.0001, scaleMax - effectiveScaleMin);
    const pts    = vals.map((v, i) => ({
      px: barX + (i / (vals.length - 1)) * barW,
      py: sparkStartY + sparkH - (Math.min(1, Math.max(0, (v - effectiveScaleMin) / vRange)) * sparkH),
    }));

    stroke(pdf, C.purple);
    pdf.setLineWidth(0.6);
    for (let i = 1; i < pts.length; i++) {
      pdf.line(pts[i - 1].px, pts[i - 1].py, pts[i].px, pts[i].py);
    }
    // Dots
    fill(pdf, C.purple);
    pts.forEach(pt => pdf.circle(pt.px, pt.py, 0.8, 'F'));

    // Month labels
    if (trendLabels?.length) {
      color(pdf, C.muted);
      pdf.setFontSize(5);
      trendLabels.forEach((lbl, i) => {
        const px = barX + (i / (trendLabels.length - 1)) * barW;
        pdf.text(lbl, px, sparkStartY + sparkH + (compact ? 3.2 : 4.5), { align: 'center' });
      });
    }
  }

  // Delta
  if (delta !== undefined) {
    color(pdf, delta >= 0 ? C.deltaGreen : C.deltaAmber);
    pdf.setFontSize(6);
    pdf.setFont('helvetica', 'bold');
    pdf.text(
      `Q Delta: ${delta >= 0 ? '+' : ''}${delta.toFixed(2)}%`,
      x + w - 4, y + h - (compact ? 1.6 : 3),
      { align: 'right' },
    );
  }
}

// ── KPI summary page ─────────────────────────────────────────────────────────

function addKpiSummaryPage(
  pdf: jsPDF,
  regionRow: Record<string, number> & { region: string },
  kpiCards: KpiCardDef[],
  monthLabels: string[],
  monthData: (Record<string, number | null> | null)[],
  variantLabel: string,
  timeLabel: string,
  generatedLabel: string,
) {
  pdf.addPage();
  drawTitleBar(pdf, `${variantLabel}  \u2014  ${regionRow.region}`, timeLabel);
  drawFooter(pdf, generatedLabel);

  const numCols = 4;
  const numRows = Math.ceil(kpiCards.length / numCols);
  const GAP     = 3;
  const GRID_X  = 5;
  const GRID_Y  = 14;
  const gridW   = PW - GRID_X * 2;
  const gridH   = PH - GRID_Y - 6;
  const cardW   = (gridW - GAP * (numCols - 1)) / numCols;
  // Cut card height by ~30% from the previous 58mm cap to reduce vertical bulk.
  const cardH   = Math.min((gridH - GAP * (numRows - 1)) / numRows, 41);

  kpiCards.forEach(({ title, field, scaleMin, target }, idx) => {
    const col   = idx % numCols;
    const row   = Math.floor(idx / numCols);
    const cx    = GRID_X + col * (cardW + GAP);
    const cy    = GRID_Y + row * (cardH + GAP);
    const value = parseFloat(((regionRow as any)[field] ?? 0).toFixed(2));

    // Match on-page sparkline logic per KPI field:
    // 1) keep only finite values for this KPI,
    // 2) if any non-zero value exists, suppress zero values as missing placeholders,
    // 3) render trend only when at least 2 points remain.
    const rawPoints = monthData
      .map((d, i) => {
        const v = d?.[field];
        return Number.isFinite(v as number) ? { i, v: Number(v) } : null;
      })
      .filter((p): p is { i: number; v: number } => p !== null);
    const hasNonZero = rawPoints.some((p) => p.v > 0);
    const filteredPoints = rawPoints.filter((p) => !hasNonZero || p.v > 0);
    const hasTrendData = filteredPoints.length >= 2;
    const filteredLabels = hasTrendData ? filteredPoints.map((p) => monthLabels[p.i]) : undefined;
    const trendVals = hasTrendData
      ? filteredPoints.map((p) => parseFloat(p.v.toFixed(2)))
      : undefined;
    const delta = hasTrendData
      ? parseFloat((filteredPoints[filteredPoints.length - 1].v - filteredPoints[0].v).toFixed(2))
      : undefined;

    drawKpiCard(pdf, cx, cy, cardW, cardH, title, value, scaleMin, 100, target,
      delta, filteredLabels, trendVals);
  });
}

// ── ECharts offscreen rendering → PNG DataURL ────────────────────────────────

async function renderEChartsDataUrl(
  rows: any[],
  config: SiteChartsConfig,
  width = PDF_CHART_PX_W,
  height = PDF_CHART_PX_H,
): Promise<string | null> {
  if (!rows.length) return null;
  try {
    const echarts = await import('echarts');
    const div = document.createElement('div');
    div.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${width}px;height:${height}px;`;
    document.body.appendChild(div);
    const chart = echarts.init(div as HTMLElement);

    // [PDF Export Debug] container vs page dimensions
    console.debug('[PDF Export] Canvas container:', width, '×', height, 'px  ratio:', (width / height).toFixed(3));
    console.debug('[PDF Export] PDF page:', PW, '×', PH, 'mm  |  chart area:', PW - 10, '×', PH - 22, 'mm  ratio:', ((PW - 10) / (PH - 22)).toFixed(3));
    console.debug('[PDF Export] Final PNG (pixelRatio×2):', width * 2, '×', height * 2, 'px');
    console.debug('[PDF Export] Effective DPI:', Math.round((width * 2) / ((PW - 10) / 25.4)));

    // Resolve theme-aware series colors — mirrors PPT's seriesMap
    const tc = getThemeAwareChartColors();
    const seriesColorMap: Record<string, string> = {
      success: tc.success,
      warning: tc.warning,
      total:   tc.total,
      error:   tc.error,
      info:    tc.info,
      active:  tc.active,
      pending: tc.pending,
      chart1:  tc.chart1,
      chart2:  tc.chart2,
      chart3:  tc.chart3,
      chart4:  tc.chart4,
      chart5:  tc.chart5,
    };
    const fallbackColor = tc.chart4;

    const timestamps = rows.map(r => {
      const t = dayjs(r.timestamp);
      return t.hour() === 0 && t.minute() === 0 ? t.format('MM/DD') : t.format('MM/DD HH:mm');
    });

    chart.setOption({
      animation: false,
      backgroundColor: '#ffffff',
      // Margins scaled for 1435×940 canvas (5px per mm of the final PDF area)
      grid: { top: 55, right: 120, bottom: 75, left: 120 },
      legend: { show: true, bottom: 10, textStyle: { fontSize: 14 } },
      xAxis: { type: 'category', data: timestamps, axisLabel: { fontSize: 12 } },
      yAxis: config.yAxes.map((axis, i) => ({
        type: 'value',
        name: axis.name,
        position: i === 0 ? 'left' : 'right',
        min: axis.min,
        max: axis.max,
        axisLabel: { formatter: axis.formatter || '{value}', fontSize: 12 },
        nameTextStyle: { fontSize: 14 },
      })),
      series: config.series.map(s => ({
        name: s.name,
        type: s.type,
        data: rows.map(r => {
          const v = r[s.dataKey];
          return v != null ? parseFloat(parseFloat(v).toFixed(2)) : null;
        }),
        yAxisIndex: s.yAxisIndex ?? 0,
        itemStyle: { color: seriesColorMap[s.color] ?? fallbackColor },
      })),
    });

    const url = chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#ffffff' });
    chart.dispose();
    document.body.removeChild(div);
    return url;
  } catch {
    return null;
  }
}

// ── Chart page ───────────────────────────────────────────────────────────────

function addChartPage(
  pdf: jsPDF,
  title: string,
  chartDataUrl: string,
  regionLabel: string,
  timeLabel: string,
  generatedLabel: string,
) {
  const imgX = 5;
  const imgY = 15;
  const imgW = PW - 10; // 287 mm
  const imgH = PH - 22; // 188 mm

  // [PDF Export Debug] placement coordinates and scale
  console.debug('[PDF Export] addChartPage:', title);
  console.debug('[PDF Export] PDF page:', PW, '×', PH, 'mm');
  console.debug('[PDF Export] Image placement: x=', imgX, 'y=', imgY, 'w=', imgW, 'h=', imgH, 'mm');
  console.debug('[PDF Export] Scale factor (mm/px):', (imgW / PDF_CHART_PX_W).toFixed(4), '×', (imgH / PDF_CHART_PX_H).toFixed(4));

  pdf.addPage();
  drawTitleBar(pdf, `${title}  \u2014  ${regionLabel}`, timeLabel);
  drawFooter(pdf, generatedLabel);
  pdf.addImage(chartDataUrl, 'PNG', imgX, imgY, imgW, imgH);
}

// ── Shared fetch helper ───────────────────────────────────────────────────────

async function fetchJson(url: string): Promise<any> {
  const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const res = await fetch(url, {
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ── Sparkline period helpers (mirrors exportPagePptx.ts) ─────────────────────

interface MonthSlice { label: string; shortLabel: string; start: string; end: string; }

/**
 * Returns the 3 months of the prior completed quarter as individual monthly
 * sparkline buckets. Oldest first.
 */
function getPriorQuarterMonths(): MonthSlice[] {
  const now = dayjs();
  const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
  const priorQStart = currentQStart.subtract(3, 'month');
  return [0, 1, 2].map((monthOffset) => {
    const start = priorQStart.add(monthOffset, 'month').startOf('month');
    const end   = start.endOf('month');
    return {
      label:      start.format('MMMM YYYY'),
      shortLabel: start.format('MMM'),
      start: start.format('YYYY-MM-DD'),
      end:   end.format('YYYY-MM-DD'),
    };
  });
}

/**
 * Returns 3 bi-monthly (2-month) buckets covering the prior 2 completed quarters.
 * Oldest first.
 */
function getPriorTwoQuartersBiMonthly(): MonthSlice[] {
  const now = dayjs();
  const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
  const twoQStart = currentQStart.subtract(6, 'month');
  return [0, 1, 2].map((bucketIndex) => {
    const start = twoQStart.add(bucketIndex * 2, 'month').startOf('month');
    const end   = start.add(1, 'month').endOf('month');
    return {
      label:      `${start.format('MMM')}–${end.format('MMM YYYY')}`,
      shortLabel: start.format('MMM'),
      start: start.format('YYYY-MM-DD'),
      end:   end.format('YYYY-MM-DD'),
    };
  });
}

/** Returns sparkline periods only for quarter modes; empty for rolling modes. */
function getSparklinePeriods(timeRange: string): MonthSlice[] {
  if (timeRange === 'lastQuarter')     return getPriorQuarterMonths();
  if (timeRange === 'lastTwoQuarters') return getPriorTwoQuartersBiMonthly();
  return [];
}

/** Returns query params for the main KPI fetch (uses start/end dates for quarters). */
function getKpiDateRange(timeRange: string): Record<string, string> {
  const now = dayjs();
  if (timeRange === 'lastQuarter') {
    const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
    const priorQStart = currentQStart.subtract(3, 'month');
    return {
      start_date: priorQStart.format('YYYY-MM-DD'),
      end_date:   currentQStart.subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
  if (timeRange === 'lastTwoQuarters') {
    const currentQStart = now.startOf('year').add(Math.floor(now.month() / 3) * 3, 'month');
    const twoQStart = currentQStart.subtract(6, 'month');
    return {
      start_date: twoQStart.format('YYYY-MM-DD'),
      end_date:   currentQStart.subtract(1, 'day').format('YYYY-MM-DD'),
    };
  }
  return { time_range: timeRange };
}

const TIME_LABELS: Record<string, string> = {
  lastWeek:        'Last 7-days',
  lastMonth:       'Last 30-days',
  lastQuarter:     'Prior Quarter',
  lastTwoQuarters: 'Prior 2 Quarters',
};

// ── Public API ────────────────────────────────────────────────────────────────

/** Options for the page-level export — identical contract to ExportPagePptxOpts. */
export interface ExportPagePdfOpts {
  tab: 'LTE' | 'NR';
  regions: string[];
  timeRange: string;
  truncate: string;
}

/**
 * Page-level export: fetches data from API, programmatically builds a full
 * A4-landscape PDF with a cover page, one KPI summary page per region,
 * and one chart page per global chart config.
 */
export async function exportPagePdf(opts: ExportPagePdfOpts): Promise<void> {
  const { tab, regions, timeRange, truncate } = opts;
  const timeLabel      = TIME_LABELS[timeRange] ?? timeRange;
  const tabLabel       = tab === 'LTE' ? 'LTE External Performance' : 'NR External Performance';
  const generatedLabel = dayjs().format('MMMM D, YYYY');
  const kpiFields      = tab === 'LTE' ? LTE_KPI_FIELDS : NR_TABLE_KPI_FIELDS;
  const kpiCards       = tab === 'LTE' ? LTE_KPI_CARDS  : NR_KPI_CARDS;
  const kpiEndpoint    = tab === 'LTE'
    ? '/v1/charts/api/lte/kpi-summary-by-region'
    : '/v1/charts/api/nr/nr-table-kpi-summary';
  const globalEndpoint = tab === 'LTE'
    ? '/v1/charts/api/lte/global-all-charts'
    : '/v1/charts/api/nr/global-all-charts';
  const chartGroups    = tab === 'LTE' ? GLOBAL_LTE_CHART_GROUPS : GLOBAL_NR_CHART_GROUPS;
  const batchKeyMap    = tab === 'LTE' ? BATCH_DATA_KEY_MAP : NR_BATCH_DATA_KEY_MAP;

  const globalParams = new URLSearchParams({ truncate, time_range: timeRange });
  regions.forEach(r => globalParams.append('region_name', r));
  const months = getSparklinePeriods(timeRange);
  const kpiDateRange = getKpiDateRange(timeRange);

  // ── Fetch all data in parallel (same as PPT) ─────────────────────────────
  const [kpiResults, globalBatch, trendResults] = await Promise.all([
    Promise.all(
      regions.map(region =>
        fetchJson(`${kpiEndpoint}?${new URLSearchParams({ ...kpiDateRange, region })}`)
          .then((rows: any[]) => ({ region, kpiRow: rows[0] ?? null }))
          .catch(() => ({ region, kpiRow: null })),
      ),
    ),
    fetchJson(`${globalEndpoint}?${globalParams}`).catch(() => ({})),
    months.length ? Promise.all(
      regions.map(region =>
        Promise.all(
          months.map(month =>
            fetchJson(`${kpiEndpoint}?${new URLSearchParams({ start_date: month.start, end_date: month.end, region })}`)
              .then((rows: any[]) => {
                if (!rows.length) return null;
                const result: Record<string, number | null> = {};
                for (const f of kpiFields) {
                  const values = rows
                    .map((r: any) => Number(r?.[f.key]))
                    .filter((v: number) => Number.isFinite(v));
                  if (!values.length) {
                    result[f.key] = null;
                    continue;
                  }
                  const avg = values.reduce((acc: number, v: number) => acc + v, 0) / values.length;
                  result[f.key] = parseFloat(avg.toFixed(2));
                }
                return result;
              })
              .catch(() => null),
          ),
        ).then(monthData => ({ region, monthData })),
      ),
    ) : Promise.resolve(regions.map(region => ({ region, monthData: [] as (Record<string, number | null> | null)[] }))),
  ]);

  // ── Build PDF ─────────────────────────────────────────────────────────────
  const pdf = new jsPDF('landscape', 'mm', 'a4');
  const kpiLabel     = tab === 'LTE' ? 'LTE KPI Summary' : 'NR KPI Summary';
  const monthLabels  = months.map(m => m.shortLabel);
  const trendByRegion = new Map(trendResults.map(r => [r.region, r.monthData]));

  // Cover page
  drawCoverPage(pdf, tabLabel, timeLabel, generatedLabel);

  // KPI summary — one page per region
  for (const { region, kpiRow } of kpiResults) {
    if (kpiRow) {
      const monthData = trendByRegion.get(region) ?? [null, null, null];
      addKpiSummaryPage(pdf, kpiRow, kpiCards, monthLabels, monthData, kpiLabel, timeLabel, generatedLabel);
    }
  }

  // Chart pages — render each chart offscreen and embed as PNG
  const regionLabel = regions.length === 1 ? regions[0] : 'All Regions';
  for (const group of chartGroups) {
    for (const config of group.configs) {
      const rows = globalBatch[(batchKeyMap as any)[config.csvFilename]] ?? null;
      if (rows?.length) {
        const dataUrl = await renderEChartsDataUrl(rows, config);
        if (dataUrl) addChartPage(pdf, config.title, dataUrl, regionLabel, timeLabel, generatedLabel);
      }
    }
  }

  pdf.save(`${tab.toLowerCase()}_performance_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
}

// ── Section-level: KPI Summary card (KpiSummaryBarChart) ─────────────────────

export interface ExportKpiCardPdfOpts {
  data: any[];
  kpiCards: KpiCardDef[];
  variant: 'lte' | 'nr-table';
  timeRange: string;
  monthLabels: string[];
  monthData: (Record<string, number | null> | null)[];
}

/** Programmatically builds a PDF from the component's in-memory state data. */
export function exportKpiSummaryCardPdf(opts: ExportKpiCardPdfOpts): void {
  const { data, kpiCards, variant, timeRange, monthLabels, monthData } = opts;
  if (!data.length) return;

  const pdf          = new jsPDF('landscape', 'mm', 'a4');
  const variantLabel = variant === 'nr-table' ? 'NR KPI Summary' : 'LTE KPI Summary';
  const timeLabel    = TIME_LABELS[timeRange] ?? timeRange;
  const generatedLabel = dayjs().format('MMMM D, YYYY');

  drawCoverPage(pdf, variantLabel, timeLabel, generatedLabel);
  for (const row of data) {
    addKpiSummaryPage(pdf, row, kpiCards, monthLabels, monthData, variantLabel, timeLabel, generatedLabel);
  }

  const slug = variant === 'nr-table' ? 'nr-kpi-summary' : 'lte-kpi-summary';
  pdf.save(`${slug}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
}

// ── Section-level: single GlobalChart card ────────────────────────────────────

export interface ExportGlobalChartPdfOpts {
  title: string;
  csvFilename: string;
  /** PNG DataURL obtained from chartRef.current.getEchartsInstance().getDataURL() */
  chartDataUrl: string;
  timeRange: string;
}

/** Builds a single-page PDF from the already-rendered ECharts DataURL. */
export function exportGlobalChartPdf(opts: ExportGlobalChartPdfOpts): void {
  const { title, csvFilename, chartDataUrl, timeRange } = opts;
  const pdf          = new jsPDF('landscape', 'mm', 'a4');
  const timeLabel    = TIME_LABELS[timeRange] ?? timeRange;
  const generatedLabel = dayjs().format('MMMM D, YYYY');

  drawTitleBar(pdf, title, timeLabel);
  drawFooter(pdf, generatedLabel);
  pdf.addImage(chartDataUrl, 'PNG', 5, 15, PW - 10, PH - 22);

  pdf.save(`${csvFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`);
}

// ── Screenshot-based helper (for DOM-ref callers) ─────────────────────────────

/**
 * Captures a DOM element via html2canvas and exports it as an A4-landscape PDF.
 * Used by standalone pages (LteChartsPage, NrChartsPage) and ChartCard which
 * operate on DOM refs rather than raw API data.
 */
export async function exportElementAsPdf(
  element: HTMLElement,
  filename?: string,
): Promise<void> {
  const bgColor =
    getComputedStyle(document.documentElement).getPropertyValue('--background').trim() || '#ffffff';

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: bgColor,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf     = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW   = pdf.internal.pageSize.getWidth();
  const pageH   = pdf.internal.pageSize.getHeight();
  const scaledW = pageW;
  const scaledH = (canvas.height / canvas.width) * pageW;

  if (scaledH <= pageH) {
    pdf.addImage(imgData, 'PNG', 0, 0, scaledW, scaledH);
  } else {
    const totalPages = Math.ceil(scaledH / pageH);
    for (let i = 0; i < totalPages; i++) {
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, -i * pageH, scaledW, scaledH);
    }
  }

  const defaultName = `export_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`;
  pdf.save(filename ?? defaultName);
}

